import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import BaseTabs from 'components/atoms/base-tabs';
import { ModuleType } from 'infrastructure/enums';
import { usePatientKeyNotes } from 'infrastructure/hooks/patient/use-key-notes';
import BaseLoader from 'components/atoms/base-loader';
import ChatDotsIcon from 'components/atoms/icons/chat-dots';
import BaseButton from 'components/atoms/baseButton';
import PencilIcon from 'components/atoms/icons/pencilIcon';
import classNames from 'classnames';
import BaseRichTextEditor from 'components/atoms/base-rich-text-editor';
import BaseRichText from 'components/atoms/base-rich-text-editor/text';

import s from './styles.module.scss';

import type {
  IPatientKeyNote,
  IUpdatePatientKeyNote,
} from 'infrastructure/interfaces';
import type { IBaseTab } from 'components/atoms/base-tabs';

interface IProps {
  patientGuid: string;
}

const KeyNote = ({
  note,
  onSave,
  module,
}: {
  note: IPatientKeyNote;
  onSave: (body: IUpdatePatientKeyNote) => void;
  module: ModuleType.RPM | ModuleType.CCM;
}) => {
  const [isEdit, setIsEdit] = useState(false);
  const { t } = useTranslation();
  const [value, setValue] = useState<string>(note.note || '');

  const handleCancel = () => {
    setIsEdit(false);
  };

  const handleEdit = () => {
    setIsEdit(true);
  };

  const handleSave = async () => {
    onSave({
      noteGuid: note.guid,
      note: value,
      module,
    });
    setIsEdit(false);
  };

  const wrapperCls = classNames(s['note-wrapper'], {
    [s.editor]: isEdit,
  });

  return (
    <div className={wrapperCls}>
      {isEdit ? (
        <BaseRichTextEditor
          height="500px"
          value={value}
          setValue={setValue}
          disabled={!isEdit}
        />
      ) : (
        <div className={s.note}>
          <BaseRichText value={value} />
        </div>
      )}
      <div className={s.footer}>
        {isEdit ? (
          <>
            <BaseButton
              width="120px"
              type="secondary"
              label={t('controls.cancel')}
              onClick={handleCancel}
            />
            <BaseButton
              width="120px"
              label={t('controls.save')}
              onClick={handleSave}
            />
          </>
        ) : (
          <BaseButton
            width="170px"
            label={t('controls.editKeyNote')}
            icon={<PencilIcon />}
            onClick={handleEdit}
          />
        )}
      </div>
    </div>
  );
};

const CallDashboardKeyNotesForm: React.FC<IProps> = (props) => {
  const { patientGuid } = props;

  const { t } = useTranslation();

  const tabs: IBaseTab<ModuleType.RPM | ModuleType.CCM>[] = [
    {
      key: ModuleType.RPM,
      label: t('labels.rpm'),
    },
    {
      key: ModuleType.CCM,
      label: t('labels.ccm'),
    },
  ];

  const [tab, setTab] = useState(tabs[0]);

  const { data, loading, update } = usePatientKeyNotes({
    patientGuid,
    module: tab.key as ModuleType,
  });

  const Empty = () => (
    <div className={s.empty}>
      <ChatDotsIcon />
      <span>{t('labels.thereAreNoKeyNotes')}</span>
    </div>
  );

  return (
    <div>
      <div className={s.head}>
        <span className={s.title}>{t('labels.keyNotes')}</span>
        <BaseTabs tabs={tabs} size="small" type="rounded" onChange={setTab} />
      </div>

      <BaseLoader loading={loading}>
        {data && data?.length ? (
          data?.map((note, i) => (
            <div key={`keynote-${i}-${note.createdAt}`}>
              <KeyNote
                module={tab.key as ModuleType.RPM | ModuleType.CCM}
                onSave={update}
                note={note}
              />
            </div>
          ))
        ) : (
          <Empty />
        )}
      </BaseLoader>
    </div>
  );
};

export default CallDashboardKeyNotesForm;
